import { useEffect, useRef } from 'react';

import { useStorageCheckoutContext } from '@root/components/checkout/context';
import { StorageCheckoutData } from '@root/components/checkout/data';
import { StorageCheckoutStep } from '@root/components/checkout/types';

import { reportError } from '@utils/monitoring';

import {
  hasChallengerLeadInfo,
  hasChallengerPickupAddress,
  isChallengerBundleStepComplete,
  isChallengerDisposalStepComplete,
  isChallengerPlanStepComplete,
  isChallengerProtectionStepComplete,
} from '../completion';

/** Persists successful step writes for the checkout session (survives remounts). */
const writtenStepsByFlowInstance = new Map<string, Set<string>>();
const inFlightStepsByFlowInstance = new Map<string, Set<string>>();

const getStepSet = (
  store: Map<string, Set<string>>,
  flowInstanceUuid: string,
): Set<string> => {
  let steps = store.get(flowInstanceUuid);
  if (!steps) {
    steps = new Set();
    store.set(flowInstanceUuid, steps);
  }
  return steps;
};

const writeChallengerFlowStep = (
  flowInstanceUuid: string,
  name: StorageCheckoutStep,
  createStepTransition: (
    name: string,
    action: string,
    meta?: { [key: string]: any },
    values?: StorageCheckoutData,
  ) => void | Promise<void>,
  values: StorageCheckoutData,
) => {
  const written = getStepSet(writtenStepsByFlowInstance, flowInstanceUuid);
  const inFlight = getStepSet(inFlightStepsByFlowInstance, flowInstanceUuid);
  if (written.has(name) || inFlight.has(name)) return;

  inFlight.add(name);
  void (async () => {
    try {
      await Promise.resolve(createStepTransition(name, 'next', {}, values));
      written.add(name);
    } catch (error: unknown) {
      reportError(error instanceof Error ? error : new Error(String(error)), {
        tags: { component: 'useChallengerFlowStepFlags', step: name },
      });
    } finally {
      inFlight.delete(name);
    }
  })();
};

/**
 * Challenger is a single plan page, so the staircase almost never moves and
 * `flow_container` never writes mid-funnel `flow_steps` rows. Flag each
 * control-comparable step name once when its completion predicate becomes true.
 *
 * Pickup address uses `address` (page 1). Contact info uses `lead_info` on
 * the confirm page — not `address_and_lead_info`, which control fires only
 * when address and lead are both captured on one step.
 *
 * @see StorageCheckoutStep names used by the classic funnel
 */
export const useChallengerFlowStepFlags = () => {
  const {
    disposalEligible,
    flowState: { createStepTransition, flowInstanceUuid, values },
  } = useStorageCheckoutContext();
  const valuesRef = useRef(values);
  valuesRef.current = values;

  const zipComplete = !!values.zipValidated;
  const planSizeComplete = values.planSize !== undefined;
  const commitmentComplete = isChallengerPlanStepComplete(values);
  const protectionComplete = isChallengerProtectionStepComplete(values);
  const bundleComplete = isChallengerBundleStepComplete(values);
  const disposalComplete = isChallengerDisposalStepComplete({
    values,
    disposalEligible,
  });
  const addressComplete = hasChallengerPickupAddress(values.address);
  const appointmentComplete = !!values.dateScheduled;

  useEffect(() => {
    const pending: Array<[StorageCheckoutStep, boolean]> = [
      [StorageCheckoutStep.Zip, zipComplete],
      [StorageCheckoutStep.PlanSize, planSizeComplete],
      [StorageCheckoutStep.Commitment, commitmentComplete],
      [StorageCheckoutStep.ProtectionPlan, protectionComplete],
      [StorageCheckoutStep.Bundle, bundleComplete],
      [StorageCheckoutStep.Disposal, disposalComplete],
      [StorageCheckoutStep.Address, addressComplete],
      [StorageCheckoutStep.Appointment, appointmentComplete],
    ];

    for (const [name, complete] of pending) {
      if (!complete) continue;
      writeChallengerFlowStep(
        flowInstanceUuid,
        name,
        createStepTransition,
        valuesRef.current,
      );
    }
  }, [
    addressComplete,
    appointmentComplete,
    bundleComplete,
    commitmentComplete,
    createStepTransition,
    disposalComplete,
    disposalEligible,
    flowInstanceUuid,
    planSizeComplete,
    protectionComplete,
    zipComplete,
  ]);
};

/** Confirm page: stamp `lead_info` when name / email / phone are captured. */
export const useChallengerLeadInfoFlowStepFlag = () => {
  const {
    flowState: { createStepTransition, flowInstanceUuid, values },
  } = useStorageCheckoutContext();
  const valuesRef = useRef(values);
  valuesRef.current = values;

  const leadInfoComplete = hasChallengerLeadInfo(values);

  useEffect(() => {
    if (!leadInfoComplete) return;
    writeChallengerFlowStep(
      flowInstanceUuid,
      StorageCheckoutStep.LeadInfo,
      createStepTransition,
      valuesRef.current,
    );
  }, [createStepTransition, flowInstanceUuid, leadInfoComplete]);
};
